import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import {
	PauseFill,
	PlayFill,
	VolumeMuteFill,
	VolumeUpFill,
} from 'react-bootstrap-icons';

import useTranslation from '../../hooks/use_translation';
import Avatar from './avatar/avatar';

const isSafari = () => {
	const user_agent = navigator.userAgent.toLowerCase();
	return user_agent.indexOf('safari') > -1 && user_agent.indexOf('chrome') < 0;
};

const VideoPlaylist = ({
	audio = false,
	children,
	className,
	playlist = [],
	poster,
	progressBar = false,
	rounded_corners,
	variant = 'responsive',
}) => {
	const reference = useRef(null);
	const { language } = useTranslation();
	const [isMuted, setIsMuted] = useState(true);
	const [isPlaying, setIsPlaying] = useState(true);
	const [overallProgress, setOverallProgress] = useState(0);
	const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

	const toggleMute = () => {
		if (reference.current) {
			reference.current.muted = !isMuted;
			setIsMuted(!isMuted);
		}
	};

	const togglePlayPause = () => {
		const player = reference.current;
		if (player) {
			if (isPlaying) {
				player.pause();
			} else {
				player.play();
			}
			setIsPlaying(!isPlaying);
		}
	};

	const jumpToNextVideo = () => {
		const player = reference.current;
		if (player) {
			if (currentVideoIndex < playlist.length - 1) {
				setCurrentVideoIndex(currentVideoIndex + 1);
				player.load();
			} else {
				setCurrentVideoIndex(0);
				player.load();
			}
		}
	};

	useEffect(() => {
		const player = reference.current;

		const updateProgress = () => {
			if (player && player.duration) {
				const videoProgress = (player.currentTime / player.duration) * 100;
				const accumulatedProgress = (currentVideoIndex / playlist.length) * 100;
				const totalProgress =
					accumulatedProgress + videoProgress / playlist.length;
				setOverallProgress(totalProgress);
			}
		};

		const handleVideoEnd = () => {
			jumpToNextVideo();
		};

		if (player) {
			player.addEventListener('timeupdate', updateProgress);
			player.addEventListener('ended', handleVideoEnd);

			if (isSafari()) {
				player.autoplay = true;
				player.controls = false;
				player.muted = isMuted;
				player.playsinline = true;
				player.setAttribute('muted', '');

				setTimeout(() => {
					const promise = player.play();
					if (promise.then) {
						promise
							.then(() => {})
							.catch(() => {
								player.style.display = 'none';
							});
					}
				}, 0);
			}
		}

		return () => {
			if (player) {
				player.removeEventListener('timeupdate', updateProgress);
				player.removeEventListener('ended', handleVideoEnd);
			}
		};
	}, [isMuted, isPlaying, currentVideoIndex, playlist.length]);

	useEffect(() => {
		const player = reference.current;
		if (player) {
			console.log(`Loading video with language: ${language}`);
			player.load();
		}
	}, [language, currentVideoIndex]);

	const getVariantClass = () => {
		switch (variant) {
			case 'square':
				return 'video__container-square';
			case '16:9':
				return 'video__container-16-9';
			case '9:16':
				return 'video__container-9-16';
			default:
				return 'video__container-responsive';
		}
	};

	const renderProgressBar = () => {
		const segmentCount = playlist.length;
		const segmentWidth = 100 / segmentCount;
		const filledSegments = Math.floor(overallProgress / segmentWidth);
		const filledWidth = ((overallProgress % segmentWidth) / segmentWidth) * 100;

		return (
			<div className="video__progress-bar">
				{playlist.map((_, index) => (
					<div
						className={`video__progress-segment ${index < filledSegments ? 'filled' : ''}`}
						key={index}
					>
						{index === filledSegments && (
							<div
								className="video__progress-bar-filled"
								style={{
									width: `${filledWidth}%`,
								}}
							></div>
						)}
					</div>
				))}
			</div>
		);
	};

	const currentVideo = playlist[currentVideoIndex];

	return (
		<section className="video">
			<div
				className={`video__container o-hidden ${rounded_corners ? 'rounded-3' : ''} ${className}`}
			>
				<div className={getVariantClass()}>
					{(progressBar || currentVideo.avatarSrc) && (
						<div className="video__header">
							{progressBar && renderProgressBar()}
							{currentVideo.avatarSrc && (
								<Avatar
									size="sm"
									src={currentVideo.avatarSrc}
									subtitle={currentVideo.avatarSubtitle}
									title={currentVideo.avatarTitle}
								/>
							)}
						</div>
					)}
					<video
						autoPlay={true}
						className={`video__container-video ${rounded_corners ? 'rounded-3' : ''}`}
						crossOrigin="anonymous"
						key={currentVideoIndex}
						loop={false}
						muted={isMuted}
						onClick={jumpToNextVideo}
						playsInline={true}
						poster={poster}
						ref={reference}
						title={currentVideo.title}
					>
						{currentVideo.mp4 && (
							<source src={currentVideo.mp4} type="video/mp4" />
						)}
						{currentVideo.webm && (
							<source src={currentVideo.webm} type="video/webm" />
						)}
						{currentVideo.subtitles && currentVideo.subtitles[language] && (
							<track
								default
								kind="subtitles"
								label={language === 'sv' ? 'Svenska' : 'English'}
								src={currentVideo.subtitles[language]}
								srcLang={language}
							/>
						)}
					</video>

					<div className="video__controls d-flex flex-lg-row flex-column gap-2">
						{audio && (
							<button
								className="video__mute-button btn-circle btn btn-dark"
								onClick={toggleMute}
							>
								{isMuted ? <VolumeMuteFill /> : <VolumeUpFill />}
							</button>
						)}
						<button
							className="video__play-pause-button btn-circle btn btn-dark"
							onClick={togglePlayPause}
						>
							{isPlaying ? <PauseFill /> : <PlayFill />}
						</button>
					</div>

					<canvas className="video__container--canvas d-none"></canvas>
					{children && (
						<div className="video__container--canvas-cover">{children}</div>
					)}
				</div>
			</div>
		</section>
	);
};

VideoPlaylist.propTypes = {
	audio: PropTypes.bool,
	children: PropTypes.node,
	className: PropTypes.string,
	playlist: PropTypes.arrayOf(
		PropTypes.shape({
			avatarSrc: PropTypes.string,
			avatarSubtitle: PropTypes.string,
			avatarTitle: PropTypes.string,
			mp4: PropTypes.string,
			subtitles: PropTypes.shape({
				en: PropTypes.string,
				sv: PropTypes.string,
			}),
			title: PropTypes.string,
			webm: PropTypes.string,
		}),
	).isRequired,
	poster: PropTypes.string,
	progressBar: PropTypes.bool,
	rounded_corners: PropTypes.bool,
	variant: PropTypes.oneOf(['responsive', 'square', '16:9', '9:16']),
};

export default VideoPlaylist;
